import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import {
  getPasswordChecks,
  isPasswordValid,
  validatePassword,
} from '../../../utils/passwordRules';

const EMPTY_PASSWORD = { password: '', confirmPassword: '' };

const AccountSettingsForm = ({ initialData, onSave, onSavePassword }) => {
  const [formData, setFormData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD);
  const [showPassword, setShowPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleChange = (field, value) => {
    // Type de personne figé : ne jamais le modifier depuis le profil
    if (field === 'customerType') return;

    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'firstName' || field === 'lastName') {
        next.fullName = `${field === 'firstName' ? value : prev.firstName || ''} ${
          field === 'lastName' ? value : prev.lastName || ''
        }`.trim();
      }
      return next;
    });
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const getFieldError = (field, info = formData) => {
    const customerType =
      initialData?.customerType === 'business' ? 'business' : 'individual';
    switch (field) {
      case 'firstName':
        if (!info.firstName?.trim()) return 'Le prénom est requis';
        if (info.firstName.trim().length < 2) return 'Le prénom doit contenir au moins 2 caractères';
        return '';
      case 'lastName':
        if (!info.lastName?.trim()) return 'Le nom est requis';
        if (info.lastName.trim().length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'phone':
        if (!info.phone?.trim()) return 'Le téléphone est requis';
        if (!/^0[1-9][0-9]{8}$/.test(String(info.phone).replace(/\s/g, ''))) {
          return 'Numéro invalide (format: 06 12 34 56 78)';
        }
        return '';
      case 'companyName':
        if (customerType !== 'business') return '';
        if (!info.companyName?.trim()) return 'La raison sociale est requise';
        if (info.companyName.trim().length < 2) {
          return 'La raison sociale doit contenir au moins 2 caractères';
        }
        return '';
      case 'siret': {
        if (customerType !== 'business') return '';
        const siretDigits = String(info.siret || '').replace(/\s/g, '');
        if (!siretDigits) return 'Le numéro SIRET est requis';
        if (!/^\d{14}$/.test(siretDigits)) return 'Le SIRET doit comporter 14 chiffres';
        return '';
      }
      default:
        return '';
    }
  };

  const validatePersonalForm = () => {
    const fields = ['firstName', 'lastName', 'phone'];
    if (initialData?.customerType === 'business') {
      fields.push('companyName', 'siret');
    }
    const errors = {};
    fields.forEach((field) => {
      const error = getFieldError(field);
      if (error) errors[field] = error;
    });
    return errors;
  };

  const handleSave = async () => {
    const errors = validatePersonalForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    setSaveError('');

    try {
      const lockedType =
        initialData?.customerType === 'business' ? 'business' : 'individual';
      await onSave({
        ...formData,
        customerType: lockedType,
        firstName: formData.firstName?.trim() || '',
        lastName: formData.lastName?.trim() || '',
        fullName: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
        companyName:
          lockedType === 'business' ? formData.companyName?.trim() || '' : '',
        siret:
          lockedType === 'business'
            ? String(formData.siret || '').replace(/\s/g, '')
            : '',
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Erreur sauvegarde profil:', err);
      setSaveError(err?.message || "Impossible d'enregistrer les modifications");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelPersonal = () => {
    setFormData(initialData);
    setFieldErrors({});
    setSaveError('');
    setIsEditing(false);
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handleSavePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    const validationError = validatePassword(
      passwordForm.password,
      passwordForm.confirmPassword
    );
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setIsSavingPassword(true);
    try {
      await onSavePassword(passwordForm.password);
      setPasswordForm(EMPTY_PASSWORD);
      setShowPassword(false);
      setIsEditingPassword(false);
      setPasswordSuccess('Mot de passe mis à jour avec succès');
    } catch (err) {
      console.error('Erreur changement mot de passe:', err);
      setPasswordError(err?.message || 'Impossible de changer le mot de passe');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleCancelPassword = () => {
    setPasswordForm(EMPTY_PASSWORD);
    setShowPassword(false);
    setPasswordError('');
    setPasswordSuccess('');
    setIsEditingPassword(false);
  };

  const passwordChecks = getPasswordChecks(passwordForm.password);
  const isBusiness =
    (initialData?.customerType || formData?.customerType) === 'business';

  return (
    <div className="space-y-8">
      {/* Informations personnelles */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-foreground">Informations personnelles</h3>
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit2"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancelPersonal} disabled={isSaving}>
                Annuler
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                loading={isSaving}
                onClick={handleSave}
              >
                Enregistrer
              </Button>
            </div>
          )}
        </div>

        {saveError && (
          <div className="p-3 rounded-lg border border-error/30 bg-error/10 text-error text-sm">
            {saveError}
          </div>
        )}

        <div
          className="flex items-center gap-3 p-3 rounded-lg border-2 border-primary bg-primary/5"
          role="status"
          aria-label={
            isBusiness ? 'Type de compte : personne morale' : 'Type de compte : personne physique'
          }
        >
          <Icon
            name={isBusiness ? 'Building2' : 'User'}
            size={20}
            color="var(--color-primary)"
          />
          <div>
            <p className="text-sm font-medium text-foreground">
              {isBusiness ? 'Personne morale' : 'Personne physique'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Type de compte non modifiable
            </p>
          </div>
        </div>

        {isBusiness && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Raison sociale"
              type="text"
              value={formData?.companyName || ''}
              onChange={(e) => handleChange('companyName', e.target.value)}
              disabled={!isEditing}
              error={fieldErrors.companyName}
              required
            />
            <Input
              label="Numéro SIRET"
              type="text"
              value={formData?.siret || ''}
              onChange={(e) => handleChange('siret', e.target.value)}
              disabled={!isEditing}
              error={fieldErrors.siret}
              required
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={isBusiness ? 'Prénom du contact' : 'Prénom'}
            type="text"
            value={formData?.firstName || ''}
            onChange={(e) => handleChange('firstName', e.target.value)}
            disabled={!isEditing}
            error={fieldErrors.firstName}
            required
          />
          <Input
            label={isBusiness ? 'Nom du contact' : 'Nom'}
            type="text"
            value={formData?.lastName || ''}
            onChange={(e) => handleChange('lastName', e.target.value)}
            disabled={!isEditing}
            error={fieldErrors.lastName}
            required
          />
        </div>

        <Input
          label="Téléphone"
          type="tel"
          value={formData?.phone || ''}
          onChange={(e) => handleChange('phone', e.target.value)}
          disabled={!isEditing}
          error={fieldErrors.phone}
          description="Pour les communications urgentes"
          required
        />
      </section>

      {/* Mot de passe */}
      <section className="space-y-4 pt-6 border-t border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-foreground">Mot de passe</h3>
          {!isEditingPassword ? (
            <Button
              variant="outline"
              size="sm"
              iconName="Key"
              iconPosition="left"
              onClick={() => {
                setIsEditingPassword(true);
                setPasswordSuccess('');
                setPasswordError('');
              }}
            >
              Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelPassword}
                disabled={isSavingPassword}
              >
                Annuler
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                loading={isSavingPassword}
                onClick={handleSavePassword}
                disabled={
                  isSavingPassword ||
                  !isPasswordValid(passwordForm.password) ||
                  passwordForm.password !== passwordForm.confirmPassword
                }
              >
                Enregistrer
              </Button>
            </div>
          )}
        </div>

        {passwordSuccess && !isEditingPassword && (
          <div className="p-3 rounded-lg border border-success/30 bg-success/10 text-success text-sm">
            {passwordSuccess}
          </div>
        )}

        {isEditingPassword ? (
          <div className="space-y-3">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="Nouveau mot de passe"
                value={passwordForm.password}
                onChange={(e) => handlePasswordChange('password', e.target.value)}
                placeholder="Nouveau mot de passe"
                autoComplete="new-password"
                disabled={isSavingPassword}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                disabled={isSavingPassword}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>

            <ul className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-3">
              {passwordChecks.map((check) => (
                <li
                  key={check.id}
                  className={`flex items-center gap-2 text-xs ${
                    check.ok ? 'text-success' : 'text-muted-foreground'
                  }`}
                >
                  <Icon name={check.ok ? 'CheckCircle' : 'Circle'} size={14} />
                  <span>{check.label}</span>
                </li>
              ))}
            </ul>

            <Input
              type={showPassword ? 'text' : 'password'}
              label="Confirmer le mot de passe"
              value={passwordForm.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              placeholder="Confirmer le mot de passe"
              autoComplete="new-password"
              disabled={isSavingPassword}
            />

            {passwordForm.confirmPassword &&
              passwordForm.password !== passwordForm.confirmPassword && (
                <p className="text-xs text-error">Les mots de passe ne correspondent pas</p>
              )}

            {passwordError && <p className="text-sm text-error">{passwordError}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Pour votre sécurité, choisissez un mot de passe robuste.
          </p>
        )}
      </section>

      {/* Préférences */}
      <section className="pt-6 border-t border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Préférences de notification</h3>
        <div className="space-y-3">
          <Checkbox
            label="Notifications par email"
            description="Recevoir les confirmations et rappels par email"
            checked={formData?.emailNotifications}
            onChange={(e) => handleChange('emailNotifications', e?.target?.checked)}
            disabled={!isEditing}
          />
          <Checkbox
            label="Notifications par SMS"
            description="Recevoir les alertes urgentes par SMS"
            checked={formData?.smsNotifications}
            onChange={(e) => handleChange('smsNotifications', e?.target?.checked)}
            disabled={!isEditing}
          />
          <Checkbox
            label="Rappels automatiques"
            description="Recevoir des rappels avant les interventions"
            checked={formData?.autoReminders}
            onChange={(e) => handleChange('autoReminders', e?.target?.checked)}
            disabled={!isEditing}
          />
          <Checkbox
            label="Offres promotionnelles"
            description="Recevoir les offres spéciales et promotions"
            checked={formData?.promotionalEmails}
            onChange={(e) => handleChange('promotionalEmails', e?.target?.checked)}
            disabled={!isEditing}
          />
        </div>
      </section>
    </div>
  );
};

export default AccountSettingsForm;
